// NOTE: gender/language/metaTag options come from './filter-field-options',
// not from '../types/contacts-filters.types' (which re-exports them). That
// module also imports FILTERABLE_FIELDS from this file, so importing the
// options from there directly would form a circular module dependency and
// crash at load time. './filter-field-options' has no imports of its own,
// so it breaks the cycle while keeping a single source of truth.
import { LANGUAGE_OPTIONS, META_TAG_OPTIONS } from './filter-field-options';

export type FieldValueType = 'string' | 'select' | 'multiSelect' | 'boolean' | 'number' | 'date';

export type FieldStorageKind = 'topLevel' | 'customAttribute';

export type FilterOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'isEmpty'
  | 'numberRange'
  | 'dateRange';

export type FilterValue =
  | string
  | string[]
  | boolean
  | number
  | [number, number]
  | { from: string; to: string }
  | '';

export interface FilterFieldOption {
  value: string;
  label: string;
}

/** Groups the segment builder's field picker into sections — see FilterRowBuilder (Task 12). */
export const FIELD_CATEGORIES = [
  'Demographics',
  'Subscription & Engagement',
  'Profile & Account',
  'Interests & Activity',
  'Acquisition',
  'Custom Fields',
] as const;

export type FieldCategory = (typeof FIELD_CATEGORIES)[number];

export interface FilterFieldConfig {
  key: string;
  label: string;
  category: FieldCategory;
  /** Dot-path Mongo field, e.g. 'Gender' or 'Source'; ignored when storageKind is 'customAttribute'. */
  mongoField: string;
  /**
   * 'topLevel' fields are real Contact columns (Language, Tags, Source,
   * EmailSubscriptionStatus, SmsSubscriptionStatus) — required for the
   * typed GraphQL `where` fast path in contacts-graphql-where.ts.
   * 'customAttribute' fields (every default and admin-created custom
   * field) query Attributes via $elemMatch instead — see
   * filter-query-builder.ts's buildFieldClause.
   */
  storageKind: FieldStorageKind;
  isArrayField?: boolean;
  valueType: FieldValueType;
  options?: FilterFieldOption[];
  /** Allowed operators, in order — operators[0] is the Contacts modal's implicit default. */
  operators: FilterOperator[];
  defaultValue?: FilterValue;
  /**
   * true = never show in the campaign wizard's Custom Segment builder or
   * RecipientsPreview's Advanced Filters, only in the Contacts page's own
   * filter. Defaults to false/absent for every existing field. See
   * SEGMENT_BUILDER_FIELDS below.
   */
  excludeFromSegmentBuilder?: boolean;
}

const toOptions = (values: readonly string[]): FilterFieldOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * Only 5 entries. Everything that used to live here as an
 * AdditionalAttributes-backed or boolean-flag field (City, Postal Code,
 * Age Range, Newsletter Subscribed, etc.) is now a seeded default custom
 * field instead — see
 * docs/superpowers/specs/2026-08-11-custom-fields-data-model-design.md §7.
 * Those fields reach the app dynamically via
 * useCustomFieldDefinitions()/useFilterableFields() (Plan A), not this
 * static array. Only fields that are genuinely native Contact columns
 * (used elsewhere in the app beyond filtering) stay here.
 */
export const FILTERABLE_FIELDS: FilterFieldConfig[] = [
  {
    key: 'language',
    label: 'Language',
    category: 'Demographics',
    mongoField: 'Language',
    storageKind: 'topLevel',
    valueType: 'select',
    options: [...LANGUAGE_OPTIONS],
    operators: ['equals', 'notEquals', 'isEmpty'],
  },
  {
    key: 'metaTag',
    label: 'Meta tag',
    category: 'Interests & Activity',
    mongoField: 'Tags',
    storageKind: 'topLevel',
    isArrayField: true,
    valueType: 'select',
    options: toOptions(META_TAG_OPTIONS),
    operators: ['contains', 'equals', 'notEquals', 'isEmpty'],
  },
  {
    key: 'source',
    label: 'Source',
    category: 'Acquisition',
    mongoField: 'Source',
    storageKind: 'topLevel',
    valueType: 'select',
    options: [
      { value: 'Manual', label: 'Manual' },
      { value: 'CsvImport', label: 'CSV Import' },
      { value: 'CrmApi', label: 'CRM API' },
      { value: 'EshopApi', label: 'eShop API' },
    ],
    operators: ['equals', 'notEquals', 'isEmpty'],
  },
  {
    key: 'emailSubscriptionStatus',
    label: 'Email subscription status',
    category: 'Subscription & Engagement',
    mongoField: 'EmailSubscriptionStatus',
    storageKind: 'topLevel',
    valueType: 'select',
    excludeFromSegmentBuilder: true,
    options: [
      { value: 'subscribed', label: 'Subscribed' },
      { value: 'unsubscribed', label: 'Unsubscribed' },
    ],
    operators: ['equals', 'notEquals', 'isEmpty'],
  },
  {
    key: 'smsSubscriptionStatus',
    label: 'SMS subscription status',
    category: 'Subscription & Engagement',
    mongoField: 'SmsSubscriptionStatus',
    storageKind: 'topLevel',
    valueType: 'select',
    excludeFromSegmentBuilder: true,
    options: [
      { value: 'subscribed', label: 'Subscribed' },
      { value: 'unsubscribed', label: 'Unsubscribed' },
    ],
    operators: ['equals', 'notEquals', 'isEmpty'],
  },
];

export const SEGMENT_BUILDER_FIELDS: FilterFieldConfig[] = FILTERABLE_FIELDS.filter(
  (field) => !field.excludeFromSegmentBuilder
);

export const getFilterableField = (key: string): FilterFieldConfig | undefined =>
  FILTERABLE_FIELDS.find((field) => field.key === key);
